import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    TextField,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Alert,
    Snackbar,
    CircularProgress,
} from '@mui/material';
import {
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    AttachFile as AttachFileIcon,
    Category as CategoryIcon,
    ArrowForward as BackIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useFetch from '../../../../../hooks/useFetch';
import useDeleteItem from '../../../../../hooks/useDeleteItem';
import RTL from '../../../../../utils/RTL';
import { toPersianDigits } from '../../../../../utils/convertNumbers';
import { API } from '../../../../../utils/apiRoutes';
import StandardTable from '../../../../common/table/StandardTable';
import PersianDatePicker from '../../../../common/PersianDatePicker';
import ExpenseForm from './ExpenseForm';
import ExpenseCategoryManager from './ExpenseCategoryManager';
import api from '../../../../../config/axiosConfig';
import { useAuth } from '../../../../../context/AuthContext';

function ExpenseRegister() {
    const theme = useTheme();
    const { user } = useAuth();
    const staffRole = (user?.staff_role?.name || '').toLowerCase();
    const canManageCategories = staffRole === 'manager' || staffRole === 'accountant' || user?.roles?.includes('manager');
    const [filters, setFilters] = useState({
        category: '',
        payee: '',
        start_date: '',
        end_date: '',
    });
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [expenseToDelete, setExpenseToDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);

    const { deleteItem, isLoading: isDeleting } = useDeleteItem();

    const categoryParams = useMemo(
        () => ({ all: canManageCategories ? 1 : 0 }),
        [canManageCategories]
    );
    const { data: categoriesData, loading: categoriesLoading, error: categoriesError, refetch: refetchCategories } = useFetch(
        API.expenseCategories,
        { params: categoryParams }
    );
    const allCategories = useMemo(
        () => categoriesData?.results || categoriesData || [],
        [categoriesData]
    );
    const activeCategories = allCategories.filter((c) => c.is_active !== false);
    const categoryMap = useMemo(() => {
        const map = new Map();
        allCategories.forEach((cat) => map.set(cat.id, cat.name));
        return map;
    }, [allCategories]);

    // Build API params
    const apiParams = useMemo(() => {
        const params = {};
        if (filters.category) params.category = filters.category;
        if (filters.payee) params.payee = filters.payee;
        if (filters.start_date) {
            const dateStr = filters.start_date instanceof Date 
                ? filters.start_date.toISOString().split('T')[0] 
                : filters.start_date;
            params.start_date = dateStr;
        }
        if (filters.end_date) {
            const dateStr = filters.end_date instanceof Date 
                ? filters.end_date.toISOString().split('T')[0] 
                : filters.end_date;
            params.end_date = dateStr;
        }
        return params;
    }, [filters]);

    const { data, loading, error, refetch } = useFetch(API.expenses, { 
        params: apiParams,
        skip: false
    });

    const expenses = data?.results || data || [];
    const totalCount = data?.count || expenses.length;
    const hasActiveFilters = Object.values(filters).some(v => v !== '');

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleClearFilters = () => {
        setFilters({
            category: '',
            payee: '',
            start_date: '',
            end_date: '',
        });
    };

    const handleCreate = () => {
        setSelectedExpense(null);
        setFormDialogOpen(true);
    };

    const handleEdit = (expense) => {
        setSelectedExpense(expense);
        setFormDialogOpen(true);
    };

    const handleDelete = (expense) => {
        setExpenseToDelete(expense);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!expenseToDelete) return;
        try {
            await deleteItem(`${API.expenses}${expenseToDelete.id}/`);
            setSnackbar({ 
                open: true, 
                message: 'هزینه با موفقیت حذف شد.', 
                severity: 'success' 
            });
            refetch();
        } catch (err) {
            setSnackbar({ 
                open: true, 
                message: err?.response?.data?.detail || 'خطا در حذف هزینه', 
                severity: 'error' 
            });
        } finally {
            setDeleteDialogOpen(false);
            setExpenseToDelete(null);
        }
    };

    const handleFormSuccess = () => {
        setFormDialogOpen(false);
        setSelectedExpense(null);
        refetch();
        setSnackbar({ 
            open: true, 
            message: selectedExpense ? 'هزینه با موفقیت ویرایش شد.' : 'هزینه با موفقیت ثبت شد.', 
            severity: 'success' 
        });
    };

    const formatCurrency = (amount) => {
        return toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount || 0));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return toPersianDigits(date.toLocaleDateString('fa-IR'));
        } catch {
            return dateString;
        }
    };

    const getCategoryLabel = (expense) => {
        if (expense?.category_name) return expense.category_name;
        return categoryMap.get(expense?.category) || '-';
    };

    const columns = [
        {
            id: 'date',
            label: 'تاریخ',
            compact: true,
            render: (expense) => (
                <Typography variant="body2">
                    {formatDate(expense.date)}
                </Typography>
            )
        },
        {
            id: 'category',
            label: 'دسته‌بندی',
            align: 'start',
            render: (expense) => (
                <Chip
                    label={getCategoryLabel(expense)}
                    size="small"
                    color="primary"
                    variant="outlined"
                />
            )
        },
        {
            id: 'payee',
            label: 'دریافت‌کننده',
            align: 'start',
            render: (expense) => (
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {expense.payee || '-'}
                </Typography>
            )
        },
        {
            id: 'amount',
            label: 'مبلغ',
            compact: true,
            render: (expense) => (
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(expense.amount)} تومان
                </Typography>
            )
        },
        {
            id: 'description',
            label: 'توضیحات',
            align: 'start',
            priority: 'secondary',
            render: (expense) => (
                <Typography 
                    variant="body2" 
                    sx={{ 
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                    title={expense.description || ''}
                >
                    {expense.description || '-'}
                </Typography>
            )
        },
        {
            id: 'attachment',
            label: 'پیوست',
            compact: true,
            priority: 'secondary',
            render: (expense) => (
                expense.attachment ? (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            window.open(expense.attachment, '_blank');
                        }}
                        aria-label="مشاهده پیوست"
                    >
                        <AttachFileIcon fontSize="small" />
                    </IconButton>
                ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                )
            )
        },
        {
            id: 'created_by',
            label: 'ایجادکننده',
            align: 'start',
            priority: 'secondary',
            render: (expense) => (
                <Typography variant="body2">
                    {expense.created_by_name || '-'}
                </Typography>
            )
        },
        {
            id: 'actions',
            label: 'عملیات',
            compact: true,
            render: (expense) => (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(expense);
                        }}
                        color="primary"
                        aria-label="ویرایش"
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(expense);
                        }}
                        color="error"
                        aria-label="حذف"
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            )
        },
    ];

    return (
        <RTL>
            <Box sx={{ p: 3 }}>
                {formDialogOpen ? (
                    /* Full-page create/edit form (drill-down) */
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                            <Button
                                startIcon={<BackIcon />}
                                variant="outlined"
                                onClick={() => {
                                    setFormDialogOpen(false);
                                    setSelectedExpense(null);
                                }}
                            >
                                بازگشت به فهرست
                            </Button>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                {selectedExpense ? 'ویرایش هزینه' : 'ثبت هزینه جدید'}
                            </Typography>
                        </Box>
                        <Paper sx={{ p: 3 }}>
                            <ExpenseForm
                                expense={selectedExpense}
                                categories={activeCategories}
                                onSuccess={handleFormSuccess}
                                onCancel={() => {
                                    setFormDialogOpen(false);
                                    setSelectedExpense(null);
                                }}
                            />
                        </Paper>
                    </Box>
                ) : (
                <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        ثبت هزینه‌ها
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mr: 'auto' }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreate}
                    >
                        ثبت هزینه جدید
                    </Button>
                    {canManageCategories && (
                        <Button
                            variant="outlined"
                            startIcon={<CategoryIcon />}
                            onClick={() => setCategoryDialogOpen(true)}
                        >
                            مدیریت دسته‌بندی‌ها
                        </Button>
                    )}
                    </Box>
                </Box>

                {/* Filters */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <FilterListIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">فیلترها</Typography>
                        {hasActiveFilters && (
                            <Button
                                size="small"
                                startIcon={<ClearIcon />}
                                onClick={handleClearFilters}
                                sx={{ mr: 'auto' }}
                            >
                                پاک کردن فیلترها
                            </Button>
                        )}
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    <Grid 
                        container 
                        spacing={0}
                        sx={{
                            '& .MuiTextField-root': { m: 0 },
                            '& .MuiFormControl-root': { m: 0 },
                        }}
                    >
                        <Grid item xs={12} sm={6} md={3} sx={{ px: 2, pb: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>دسته‌بندی</InputLabel>
                                <Select
                                    value={filters.category}
                                    label="دسته‌بندی"
                                    onChange={(e) => handleFilterChange('category', e.target.value || '')}
                                >
                                    <MenuItem value="">همه</MenuItem>
                                    {(canManageCategories ? allCategories : activeCategories).map((choice) => (
                                        <MenuItem key={choice.id} value={choice.id}>
                                            {choice.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} sx={{ px: 2, pb: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="دریافت‌کننده"
                                value={filters.payee}
                                onChange={(e) => handleFilterChange('payee', e.target.value)}
                                placeholder="جستجو..."
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} sx={{ px: 2, pb: 2 }}>
                            <PersianDatePicker
                                label="از تاریخ"
                                value={filters.start_date ? new Date(filters.start_date) : null}
                                onChange={(date) => {
                                    if (date && !isNaN(date.getTime())) {
                                        handleFilterChange('start_date', date.toISOString().split('T')[0]);
                                    } else {
                                        handleFilterChange('start_date', '');
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} sx={{ px: 2, pb: 2 }}>
                            <PersianDatePicker
                                label="تا تاریخ"
                                value={filters.end_date ? new Date(filters.end_date) : null}
                                onChange={(date) => {
                                    if (date && !isNaN(date.getTime())) {
                                        handleFilterChange('end_date', date.toISOString().split('T')[0]);
                                    } else {
                                        handleFilterChange('end_date', '');
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Results Count */}
                {!loading && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            تعداد نتایج: {toPersianDigits(totalCount)}
                        </Typography>
                    </Box>
                )}

                {/* Expenses Table */}
                {!loading && (
                    <StandardTable
                        columns={columns}
                        rows={expenses}
                        loading={false}
                        error={error}
                        emptyText="هزینه‌ای یافت نشد."
                        ariaLabel="جدول هزینه‌ها"
                        onRowClick={handleEdit}
                    />
                )}
                </>
                )}

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => {
                        setDeleteDialogOpen(false);
                        setExpenseToDelete(null);
                    }}
                >
                    <DialogTitle>تأیید حذف</DialogTitle>
                    <DialogContent>
                        <Typography>
                            آیا از حذف این هزینه اطمینان دارید؟ این عمل قابل بازگشت نیست.
                        </Typography>
                        {expenseToDelete && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    دسته‌بندی: {getCategoryLabel(expenseToDelete)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    دریافت‌کننده: {expenseToDelete.payee}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    مبلغ: {formatCurrency(expenseToDelete.amount)} تومان
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button 
                            onClick={() => {
                                setDeleteDialogOpen(false);
                                setExpenseToDelete(null);
                            }}
                        >
                            انصراف
                        </Button>
                        <Button 
                            onClick={confirmDelete} 
                            color="error" 
                            variant="contained"
                            disabled={isDeleting}
                        >
                            حذف
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={categoryDialogOpen}
                    onClose={() => setCategoryDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>مدیریت دسته‌بندی هزینه‌ها</DialogTitle>
                    <DialogContent>
                        <ExpenseCategoryManager
                            onClose={() => setCategoryDialogOpen(false)}
                            categories={Array.isArray(allCategories) ? allCategories : []}
                            loading={categoriesLoading && !(allCategories?.length)}
                            fetchError={categoriesError || ''}
                            onRefresh={refetchCategories}
                        />
                    </DialogContent>
                </Dialog>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert 
                        onClose={() => setSnackbar({ ...snackbar, open: false })} 
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </RTL>
    );
}

export default ExpenseRegister;

